import { io, Socket } from 'socket.io-client';
import { REALTIME_EVENTS } from './realtime.constants';
import type {
  RealtimeClient,
  RealtimeConfig,
  SocketStatus,
  EventHandler,
  ChannelSubscription,
  AckCallback,
} from './realtime.types';

const GENERIC_DEFAULTS = {
  reconnection: true,
  reconnectionDelay: 1000,
  reconnectionDelayMax: 5000,
  reconnectionAttempts: 5,
  autoConnect: false,
  transports: ['websocket', 'polling'],
  debug: false,
} satisfies Partial<RealtimeConfig>;

// Events routed through the local bus, never sent to/from the socket server.
const isLocalEvent = (event: string) => event.startsWith('realtime:');

class RealtimeClientImpl implements RealtimeClient {
  private socket: Socket | null = null;
  private config: RealtimeConfig;
  private status: SocketStatus = 'disconnected';
  private subscriptions: Map<string, ChannelSubscription<unknown>> = new Map();
  // Local event bus for internal status events (realtime:connected, etc.)
  private localListeners: Map<string, Set<EventHandler<unknown>>> = new Map();
  // Maps original handler → wrapped closure so off(event, handler) removes the right socket ref.
  private wrappers: Map<string, Map<EventHandler<unknown>, (data: unknown) => void>> = new Map();

  constructor(config: { url: string } & Partial<RealtimeConfig>) {
    this.config = { ...GENERIC_DEFAULTS, ...config } as RealtimeConfig;

    if (this.config.autoConnect) {
      this.connect().catch((error) => {
        console.error('[Realtime] Auto-connect failed:', error);
      });
    }
  }

  async connect(): Promise<void> {
    if (this.socket?.connected) return;

    // A socket instance already exists but is disconnected (e.g. a `connect()` racing a
    // `reconnect()`): reopen THAT instance instead of creating a second io(), which would
    // orphan the first socket and drop every handler registered on it.
    if (this.socket) {
      const existing = this.socket;
      this.status = 'connecting';
      return new Promise((resolve, reject) => {
        existing.once('connect', () => resolve());
        existing.once('connect_error', (error: Error | unknown) => reject(error));
        existing.connect();
      });
    }

    return new Promise((resolve, reject) => {
      try {
        this.status = 'connecting';

        const { auth } = this.config;
        const resolvedAuth = auth?.tokenFn
          ? (cb: (o: object) => void) => cb({ token: auth.tokenFn!() ?? '' })
          : auth?.token
            ? { token: auth.token }
            : (auth?.headers ?? {});

        const socketConfig = {
          path: this.config.path,
          query: this.config.query,
          reconnection: this.config.reconnection,
          reconnectionDelay: this.config.reconnectionDelay,
          reconnectionDelayMax: this.config.reconnectionDelayMax,
          reconnectionAttempts: this.config.reconnectionAttempts,
          transports: this.config.transports as ('websocket' | 'polling')[],
          auth: resolvedAuth,
        };

        this.socket = io(this.config.url, socketConfig);

        this.socket.on('connect', () => {
          this.status = 'connected';
          if (this.config.debug) console.log('[Realtime] Connected');
          this.localDispatch(REALTIME_EVENTS.CONNECTED);
          resolve();
        });

        this.socket.on('disconnect', () => {
          this.status = 'disconnected';
          if (this.config.debug) console.log('[Realtime] Disconnected');
          this.localDispatch(REALTIME_EVENTS.DISCONNECTED);
        });

        this.socket.on('reconnecting', () => {
          this.status = 'reconnecting';
          if (this.config.debug) console.log('[Realtime] Reconnecting');
          this.localDispatch(REALTIME_EVENTS.RECONNECTING);
        });

        this.socket.on('error', (error: Error | unknown) => {
          this.status = 'error';
          if (this.config.debug) console.error('[Realtime] Error:', error);
          this.localDispatch(REALTIME_EVENTS.ERROR, error);
          reject(error);
        });

        this.socket.on('connect_error', (error: Error | unknown) => {
          this.status = 'error';
          if (this.config.debug) console.error('[Realtime] Connection error:', error);
          this.localDispatch(REALTIME_EVENTS.ERROR, error);
          reject(error);
        });
      } catch (error) {
        this.status = 'error';
        reject(error);
      }
    });
  }

  disconnect(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
    this.status = 'disconnected';
  }

  reconnect(): void {
    // Re-handshake on the SAME socket instance: socket.io keeps `.on()` handlers
    // registered across a manual disconnect→connect on the same Socket (a fresh io()
    // would drop them), and re-invokes the `auth` function so a rotated token is sent.
    if (!this.socket) {
      void this.connect().catch((error) => {
        console.error('[Realtime] Reconnect failed:', error);
      });
      return;
    }
    this.status = 'connecting';
    this.socket.disconnect();
    this.socket.connect();
  }

  isConnected(): boolean {
    return this.status === 'connected' && this.socket?.connected === true;
  }

  getStatus(): SocketStatus {
    return this.status;
  }

  emit(event: string, data?: unknown, ack?: AckCallback): void {
    if (isLocalEvent(event)) {
      this.localDispatch(event, data);
      return;
    }
    if (!this.socket) {
      if (this.config.debug)
        console.warn(`[Realtime] Cannot emit '${event}': socket not connected`);
      return;
    }
    if (ack) {
      this.socket.emit(event, data, ack);
    } else {
      this.socket.emit(event, data);
    }
  }

  on<T = unknown>(event: string, handler: EventHandler<T>): () => void {
    if (isLocalEvent(event)) {
      return this.localOn(event, handler as EventHandler<unknown>);
    }
    if (!this.socket) {
      if (this.config.debug)
        console.warn(`[Realtime] Cannot listen to '${event}': socket not connected`);
      return () => {};
    }

    const wrappedHandler = (data: T) => {
      try {
        const result = handler(data);
        if (result instanceof Promise) {
          result.catch((error) => {
            console.error(`[Realtime] Error in handler for '${event}':`, error);
          });
        }
      } catch (error) {
        console.error(`[Realtime] Error in handler for '${event}':`, error);
      }
    };

    if (!this.wrappers.has(event)) this.wrappers.set(event, new Map());
    this.wrappers
      .get(event)!
      .set(handler as EventHandler<unknown>, wrappedHandler as (data: unknown) => void);

    this.socket.on(event, wrappedHandler);

    return () => {
      this.socket?.off(event, wrappedHandler);
      this.wrappers.get(event)?.delete(handler as EventHandler<unknown>);
    };
  }

  once<T = unknown>(event: string, handler: EventHandler<T>): void {
    if (isLocalEvent(event)) {
      const unsub = this.localOn(event, (data) => {
        unsub();
        handler(data as T);
      });
      return;
    }
    if (!this.socket) {
      if (this.config.debug)
        console.warn(`[Realtime] Cannot listen once to '${event}': socket not connected`);
      return;
    }

    const wrappedHandler = (data: T) => {
      try {
        const result = handler(data);
        if (result instanceof Promise) {
          result.catch((error) => {
            console.error(`[Realtime] Error in once handler for '${event}':`, error);
          });
        }
      } catch (error) {
        console.error(`[Realtime] Error in once handler for '${event}':`, error);
      }
    };

    this.socket.once(event, wrappedHandler);
  }

  off(event: string, handler?: EventHandler): void {
    if (isLocalEvent(event)) {
      if (handler) {
        this.localListeners.get(event)?.delete(handler as EventHandler<unknown>);
      } else {
        this.localListeners.delete(event);
      }
      return;
    }
    if (!this.socket) return;

    if (handler) {
      const wrapped = this.wrappers.get(event)?.get(handler as EventHandler<unknown>);
      if (wrapped) this.socket.off(event, wrapped);
      this.wrappers.get(event)?.delete(handler as EventHandler<unknown>);
    } else {
      this.socket.off(event);
      this.wrappers.delete(event);
    }
  }

  subscribe<T = unknown>(
    channel: string,
    events: string[],
    handler: EventHandler<T>
  ): ChannelSubscription<T> {
    const unsubscribers = events.map((event) => this.on(`${channel}:${event}`, handler));

    const subscription: ChannelSubscription<T> = {
      channel,
      events,
      handler,
      unsubscribe: () => {
        unsubscribers.forEach((unsub) => unsub());
        this.subscriptions.delete(channel);
      },
    };

    this.subscriptions.set(channel, subscription as ChannelSubscription<unknown>);
    return subscription;
  }

  unsubscribe(channel: string): void {
    this.subscriptions.get(channel)?.unsubscribe();
  }

  isSubscribed(channel: string): boolean {
    return this.subscriptions.has(channel);
  }

  getSubscribedChannels(): string[] {
    return Array.from(this.subscriptions.keys());
  }

  private localOn(event: string, handler: EventHandler<unknown>): () => void {
    if (!this.localListeners.has(event)) this.localListeners.set(event, new Set());
    this.localListeners.get(event)!.add(handler);
    return () => this.localListeners.get(event)?.delete(handler);
  }

  private localDispatch(event: string, data?: unknown): void {
    this.localListeners.get(event)?.forEach((handler) => {
      try {
        handler(data);
      } catch (error) {
        console.error(`[Realtime] Error in local handler for '${event}':`, error);
      }
    });
  }
}

export function createSocketIOClient(
  config: { url: string } & Partial<RealtimeConfig>
): RealtimeClient {
  return new RealtimeClientImpl(config);
}
